import { DialogRef } from '@angular/cdk/dialog'
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'

import { HlmButtonImports } from '@yoshu-lib/ui/button/src'
import { HlmDialogImports } from '@yoshu-lib/ui/dialog/src'
import { HlmFormFieldImports } from '@yoshu-lib/ui/form-field/src'
import { HlmInputImports } from '@yoshu-lib/ui/input/src'
import { HlmLabelImports } from '@yoshu-lib/ui/label/src'

import { ProtocolSession } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.model'
import { ProtocolSessionsService } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.service'

import { TranslatePipe } from '@ngx-translate/core'
import { injectBrnDialogContext } from '@spartan-ng/brain/dialog'

/** Feedback key Learning reads for the missing-FM create signal. */
export const FM_NOT_LISTED_COMMENT_KEY = 'fm_not_listed_comment'

export interface MissingFailureModeFeedbackDialogContext {
  sessionId: string
  /** Hide the default dialog X; Cancel is the only dismiss path. */
  $hideCloseButton?: boolean
}

@Component({
  selector: 'app-missing-failure-mode-feedback-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    HlmDialogImports,
    HlmFormFieldImports,
    HlmInputImports,
    HlmLabelImports,
    HlmButtonImports
  ],
  host: {
    class: 'block w-full min-w-0 max-w-full'
  },
  templateUrl: './missing-failure-mode-feedback-dialog.component.html'
})
export class MissingFailureModeFeedbackDialogComponent {
  private readonly dialogRef = inject(DialogRef)
  private readonly fb = inject(FormBuilder)
  private readonly sessionsService = inject(ProtocolSessionsService)

  readonly context = injectBrnDialogContext<MissingFailureModeFeedbackDialogContext>()
  readonly isLoading = signal(false)

  readonly form = this.fb.nonNullable.group({
    comment: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(1)])
  })

  cancel() {
    this.dialogRef.close(false)
  }

  submitForm() {
    if (this.form.invalid || this.isLoading()) return
    const comment = this.form.getRawValue().comment.trim()
    if (!comment) {
      this.form.controls.comment.setErrors({ required: true })
      return
    }

    this.isLoading.set(true)
    this.sessionsService
      .updateSession(this.context.sessionId, {
        feedback_answers: { [FM_NOT_LISTED_COMMENT_KEY]: comment }
      })
      .subscribe({
        next: (session: ProtocolSession) => {
          this.isLoading.set(false)
          this.dialogRef.close(session)
        },
        error: () => {
          this.isLoading.set(false)
        }
      })
  }
}
